import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import LangLink from '../../components/common/LangLink';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import SEOHead from '../../components/common/SEOHead';
import AuthLayout, { AUTH_INPUT_CLASS, AUTH_BUTTON_CLASS } from './AuthLayout';

const ERROR_MESSAGES = {
    INVALID_CREDENTIALS: { fr: 'Identifiants incorrects.', en: 'Invalid credentials.' },
    NOT_VERIFIED:        { fr: 'Veuillez activer votre compte via le lien reçu par courriel.', en: 'Please activate your account using the link sent to your email.' },
    SERVER_ERROR:        { fr: 'Erreur serveur. Veuillez réessayer plus tard.', en: 'Server error. Please try again later.' },
    NETWORK_ERROR:       { fr: 'Impossible de joindre le serveur. Vérifiez votre connexion.', en: 'Unable to reach the server. Check your connection.' },
    LOGIN_FAILED:        { fr: 'Échec de la connexion.', en: 'Login failed.' }
};

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [errorCode, setErrorCode] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const { login, sessionReplaced, clearSessionReplaced } = useAuth();
    const { language } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const fr = language === 'fr';

    const t = (code) => ERROR_MESSAGES[code]?.[language] || ERROR_MESSAGES[code]?.fr || code;

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('activated') === 'true') {
            setSuccessMessage(fr
                ? 'Votre compte a été activé avec succès. Vous pouvez maintenant vous connecter.'
                : 'Your account has been activated successfully. You can now log in.');
        } else if (params.get('passwordReset') === 'true') {
            setSuccessMessage(fr
                ? 'Mot de passe mis à jour. Connectez-vous avec votre nouveau mot de passe.'
                : 'Password updated. Log in with your new password.');
        }
    }, [location, fr]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(''); setErrorCode(''); setSuccessMessage('');
        setLoading(true);
        try {
            const result = await login(email, password);
            if (result.success) {
                clearSessionReplaced();
                const params = new URLSearchParams(location.search);
                navigate(params.get('returnTo') || '/dashboard');
            } else {
                const code = result.errorCode || 'LOGIN_FAILED';
                setErrorCode(code);
                setError(t(code));
            }
        } catch (err) {
            setError(fr ? 'Une erreur est survenue. Veuillez réessayer.' : 'An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout
            title={fr ? 'Connectez-vous' : 'Log in'}
            subtitle={fr ? 'Accédez à votre espace de formation' : 'Access your training platform'}
            footer={
                <>
                    <div className="flex flex-col gap-3">
                        <p className="text-center text-gray-500 dark:text-[#a9b1bd]" style={{ fontSize: '1.2em' }}>
                            {fr ? 'Pas encore de compte ?' : "Don't have an account?"}
                        </p>
                        <LangLink to="/signup" className={AUTH_BUTTON_CLASS}>
                            {fr ? 'Créer un compte' : 'Create an account'}
                        </LangLink>
                    </div>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-center md:gap-3">
                        <LangLink to="/forgot-password" className="text-sm text-[#0061FE] dark:text-[#71a7ff] hover:text-[#0052db] dark:hover:text-white font-medium transition">
                            {fr ? 'Mot de passe oublié ?' : 'Forgot password?'}
                        </LangLink>
                        <span className="hidden md:inline text-gray-400 dark:text-white/30">|</span>
                        <LangLink
                            to={`/resend-verification${email ? `?email=${encodeURIComponent(email)}` : ''}`}
                            className="text-xs text-gray-500 dark:text-white/40 hover:text-gray-700 dark:hover:text-white/70 transition"
                        >
                            {fr ? "Courriel de validation non reçu ?" : "Didn't receive the verification email?"}
                        </LangLink>
                    </div>
                    <LangLink to="/" className="block text-xs text-gray-500 dark:text-white/40 hover:text-gray-700 dark:hover:text-white/70 transition">
                        {fr ? '← Retour à l\'accueil' : '← Back to home'}
                    </LangLink>
                </>
            }
        >
            <SEOHead title={fr ? 'Connexion' : 'Login'} canonical="/login" noindex />

            {sessionReplaced && (
                <div className="mb-5 p-3.5 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-xl">
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                        {fr
                            ? 'Vous avez été déconnecté car une connexion a été détectée sur un autre appareil.'
                            : 'You have been logged out because a login was detected on another device.'}
                    </p>
                </div>
            )}

            {successMessage && (
                <div className="mb-5 p-3.5 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 rounded-xl">
                    <p className="text-sm text-green-700 dark:text-green-300">{successMessage}</p>
                </div>
            )}

            {error && (
                <div className="mb-5 p-3.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl">
                    <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                    {errorCode === 'NOT_VERIFIED' && (
                        <LangLink
                            to={`/resend-verification${email ? `?email=${encodeURIComponent(email)}` : ''}`}
                            className="inline-block mt-2 text-sm text-[#0061FE] dark:text-[#71a7ff] hover:text-[#0052db] dark:hover:text-white font-medium underline underline-offset-4"
                        >
                            {fr ? "Renvoyer le courriel d'activation" : 'Resend activation email'}
                        </LangLink>
                    )}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                    <label htmlFor="email" className="block text-xs font-semibold text-gray-700 dark:text-white/70 mb-2 uppercase tracking-wider">
                        {fr ? 'Adresse courriel' : 'Email address'}
                    </label>
                    <input
                        id="email" type="email" value={email}
                        onChange={(e) => setEmail(e.target.value)} required
                        className={AUTH_INPUT_CLASS}
                        placeholder={fr ? 'vous@exemple.com' : 'you@example.com'}
                        autoComplete="email"
                    />
                </div>

                <div>
                    <label htmlFor="password" className="block text-xs font-semibold text-gray-700 dark:text-white/70 mb-2 uppercase tracking-wider">
                        {fr ? 'Mot de passe' : 'Password'}
                    </label>
                    <input
                        id="password" type="password" value={password}
                        onChange={(e) => setPassword(e.target.value)} required
                        className={AUTH_INPUT_CLASS}
                        placeholder="••••••••"
                        autoComplete="current-password"
                    />
                </div>

                <button type="submit" disabled={loading} className={`${AUTH_BUTTON_CLASS} mt-2`}>
                    {loading
                        ? (fr ? 'Connexion...' : 'Logging in...')
                        : (fr ? 'Se connecter' : 'Log in')}
                </button>
            </form>
        </AuthLayout>
    );
}
